import React, { useState, useEffect, useCallback } from 'react';
import { Zap, Clock, Calendar } from 'lucide-react';
import { formatVND } from '../../utils/formatters';
import apiService from '../../components/services/apiService';

export default function FlashSale({ addToCart }) {
  const [flashSaleItems, setFlashSaleItems] = useState([]);
  const [saleStatus, setSaleStatus] = useState('LOADING'); // 'ACTIVE', 'UPCOMING', 'ENDED'
  const [campaignTitle, setCampaignTitle] = useState('Chương trình Flash Sale');
  const [loading, setLoading] = useState(true);
  
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  const fetchFlashSaleData = useCallback(async () => {
    try {
      const timestamp = new Date().getTime();
      // 1. Kiểm tra phiên ACTIVE
      let response = await apiService.get(`/flash-sale/current?_t=${timestamp}`);
      let data = response.data !== undefined ? response.data : response;
      let currentStatus = 'ACTIVE';

      // 2. Nếu không có ACTIVE, kiểm tra phiên UPCOMING
      if (!Array.isArray(data) || data.length === 0) {
        response = await apiService.get(`/flash-sale/upcoming?_t=${timestamp}`);
        data = response.data !== undefined ? response.data : response;

        if (Array.isArray(data) && data.length > 0) {
          currentStatus = 'UPCOMING';
        } else {
          // 3. Nếu không có cả ACTIVE lẫn UPCOMING -> Lấy phiên ENDED gần nhất
          response = await apiService.get(`/flash-sale/ended?_t=${timestamp}`);
          data = response.data !== undefined ? response.data : response;
          currentStatus = 'ENDED';
        }
      }

      setSaleStatus(currentStatus);
      setFlashSaleItems(Array.isArray(data) ? data : []);

      if (Array.isArray(data) && data.length > 0) {
        if (data[0].campaignTitle) {
          setCampaignTitle(data[0].campaignTitle);
        }

        // Chỉ đếm ngược khi là ACTIVE hoặc UPCOMING
        if (currentStatus !== 'ENDED') {
          const targetTimeStr = currentStatus === 'UPCOMING' ? data[0].campaignStartTime : data[0].campaignEndTime;
          if (targetTimeStr) {
            const targetTimeMs = new Date(targetTimeStr).getTime();
            startCountdown(targetTimeMs, currentStatus);
          }
        }
      }
    } catch (error) {
      console.error('Lỗi khi tải dữ liệu Flash Sale:', error);
      setSaleStatus('ENDED');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 1. Gọi lần đầu khi mở trang
    fetchFlashSaleData();

    // 2. Lắng nghe sự kiện từ CartContext
    const handleFlashSaleUpdate = () => {
      console.log(">>> Đã nhận được tín hiệu cập nhật Flash Sale từ giỏ hàng!");
      fetchFlashSaleData(); // Gọi lại API lấy dữ liệu mới
    };

    window.addEventListener('flashSaleUpdated', handleFlashSaleUpdate);

    // Cleanup khi component unmount
    return () => {
      window.removeEventListener('flashSaleUpdated', handleFlashSaleUpdate);
    };
  }, [fetchFlashSaleData]);

  const startCountdown = (targetTimeMs, statusType) => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetTimeMs - now;

      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        // Hết giờ thì tự động load lại để chuyển trạng thái
        fetchFlashSaleData(); 
      } else {
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        setTimeLeft({ hours, minutes, seconds });
      }
    }, 1000);

    return () => clearInterval(timer);
  };

  const handleActionClick = async (item) => {
    if (saleStatus === 'UPCOMING') {
      alert('Chương trình chưa bắt đầu. Vui lòng đợi đến giờ mở bán!');
      return;
    }
    if (saleStatus === 'ENDED') {
      alert('Chương trình Flash Sale này đã kết thúc!');
      return;
    }
    try {
      // 1. Gọi API phương thức POST với đúng tên param mà Controller yêu cầu
      await apiService.post(`/flash-sale/grab?flashSaleProductId=${item.id}&quantity=1`);

      // 2. Thêm vào giỏ hàng ở phía Frontend
      if (addToCart) {
        const productForCart = {
          ...item,
          id: item.productId, 
          flashSaleId: item.id,
          price: item.flashSalePrice,
          image: item.image,
        };
        addToCart(productForCart, 1);
      }

      // 3. Cập nhật lại state giao diện ngay lập tức để tăng số lượng đã bán (soldQuantity)
      setFlashSaleItems(prevItems => 
        prevItems.map(i => {
          if (i.id === item.id) {
            const newSold = (i.soldQuantity || 0) + 1;
            return { ...i, soldQuantity: newSold };
          }
          return i;
        })
      );

      alert('Săn Flash Sale thành công! 🎉 Tiến hành thanh toán thôi.');
    } catch (error) {
      console.error('Lỗi khi mua sản phẩm Flash Sale:', error);
      // Lấy thông báo lỗi trả về từ ResponseEntity.badRequest().body(...) ở Backend
      const errorMsg = error.response?.data || 'Sản phẩm Flash Sale đã hết hàng hoặc vượt quá số lượng cho phép!';
      alert(errorMsg);
      
      // Đồng bộ lại dữ liệu từ server khi có lỗi hết kho
      fetchFlashSaleData();
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-white bg-slate-950">Đang tải chương trình Flash Sale...</div>;
  }

  // Nếu hoàn toàn không có dữ liệu gì trong hệ thống
  if (flashSaleItems.length === 0) {
    return null;
  }

  return (
    <section id="flash-sale-section" className="py-12 bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-950 text-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 border-b border-slate-800 pb-6">
          <div className="flex items-center space-x-3">
            <div className={`p-3 rounded-2xl border ${saleStatus === 'UPCOMING' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : saleStatus === 'ENDED' ? 'bg-rose-500/20 text-rose-400 border-rose-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'}`}>
              {saleStatus === 'UPCOMING' ? <Calendar className="w-7 h-7" /> : <Zap className="w-7 h-7 fill-amber-400" />}
            </div>
            <div>
              <span className={`text-xs font-bold uppercase tracking-widest ${saleStatus === 'UPCOMING' ? 'text-blue-400' : saleStatus === 'ENDED' ? 'text-rose-400' : 'text-amber-400'}`}>
                {saleStatus === 'UPCOMING' ? '⏳ SẮP DIỄN RA' : saleStatus === 'ENDED' ? '⏰ ĐÃ KẾT THÚC' : '⚡ SỐ LƯỢNG CÓ HẠN'}
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold font-serif">{campaignTitle}</h2>
            </div>
          </div>

          {/* Countdown Clock (Chỉ hiện khi ACTIVE hoặc UPCOMING) */}
          {saleStatus !== 'ENDED' && (
            <div className="flex items-center space-x-3 bg-slate-900/85 backdrop-blur-md px-5 py-2.5 rounded-2xl border border-slate-700">
              <Clock className={`w-4 h-4 ${saleStatus === 'UPCOMING' ? 'text-blue-400' : 'text-amber-400'}`} />
              <span className="text-xs font-medium text-slate-300">
                {saleStatus === 'UPCOMING' ? 'Bắt đầu sau:' : 'Kết thúc sau:'}
              </span>
              <div className={`flex items-center space-x-2 font-mono font-bold ${saleStatus === 'UPCOMING' ? 'text-blue-400' : 'text-amber-400'}`}>
                <span className="bg-slate-950 px-2 py-1 rounded border border-slate-800">{String(timeLeft.hours).padStart(2, '0')}</span>
                <span>:</span>
                <span className="bg-slate-950 px-2 py-1 rounded border border-slate-800">{String(timeLeft.minutes).padStart(2, '0')}</span>
                <span>:</span>
                <span className="bg-slate-950 px-2 py-1 rounded border border-slate-800">{String(timeLeft.seconds).padStart(2, '0')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Danh sách sản phẩm: Tự động làm mờ (opacity-60 grayscale) nếu là ENDED */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition duration-500`}>
          {flashSaleItems.map((item) => {
            const percent = item.stockLimit > 0 ? Math.round((item.soldQuantity / item.stockLimit) * 100) : 0;
            
            {/* Tính toán số lượng tồn kho còn lại */}
            const remainingStock = (item.stockLimit || 0) - (item.soldQuantity || 0);
            const isOutOfStock = remainingStock <= 0;

            return (
              <div key={item.id} className="bg-slate-900/60 rounded-2xl p-4 border border-slate-800 flex gap-4 items-center group">
                <div className="w-24 h-24 rounded-xl overflow-hidden bg-slate-950 shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>

                <div className="flex-1 space-y-2">
                  <h4 className="font-bold text-sm text-white line-clamp-1">{item.name}</h4>
                  
                  <div className="flex items-baseline space-x-2">
                    <span className="text-base font-extrabold text-amber-400">{formatVND(item.flashSalePrice)}</span>
                    <span className="text-xs text-slate-500 line-through">{formatVND(item.originalPrice)}</span>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-[11px] text-slate-400">
                      <span>{saleStatus === 'UPCOMING' ? 'Mở bán' : `Đã bán: ${percent}%`}</span>
                      <span>{saleStatus === 'UPCOMING' ? `${item.stockLimit} suất` : `Còn lại: ${Math.max(0, remainingStock)}`}</span>
                    </div>
                    <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full" style={{ width: `${percent}%` }}></div>
                    </div>
                  </div>

                  {/* Nút bấm mua hàng với điều kiện khóa khi hết hàng */}
                  <button
                    onClick={() => !isOutOfStock && handleActionClick(item)}
                    disabled={saleStatus === 'ENDED' || saleStatus === 'UPCOMING' || isOutOfStock}
                    className={`w-full mt-1 text-xs font-bold py-1.5 rounded-lg transition ${
                      saleStatus === 'UPCOMING'
                        ? 'bg-blue-600/50 text-blue-200 cursor-not-allowed'
                        : saleStatus === 'ENDED' || isOutOfStock
                        ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                        : 'bg-amber-500 hover:bg-amber-400 text-slate-950 cursor-pointer'
                    }`}
                  >
                    {saleStatus === 'UPCOMING' 
                      ? '⏳ Sắp ra mắt' 
                      : saleStatus === 'ENDED' 
                      ? 'Đã Hết Hạn' 
                      : isOutOfStock 
                      ? 'Đã Hết Hàng' 
                      : 'Mua Ngay'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}